import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { ChevronDown, User, LogOut } from 'react-feather';
import { useNavigate, useLocation } from 'react-router-dom';
import navbarLogo from '../../../assets/navbar-logo.png';

// --- STYLED COMPONENTS (CSS DENTRO DE JS) ---

const HeaderContainer = styled.header`
  background-color: ${p => p.theme.colors.background.paper}; /* White/Dark background */
  box-shadow: ${p => p.theme.shadows.sm}; /* Subtle shadow */
  z-index: 1000;
  width: 100%;
  position: absolute;
  @media (min-width: 1024px) {
    position: fixed;
  }
  top: 0;
  left: 0;
  padding-top: env(safe-area-inset-top); /* iPhone notch support */
  /* Ensure minimum height */
  min-height: calc(60px + env(safe-area-inset-top)); 
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  height: 60px;
  width: 100%;
  box-sizing: border-box;
`;

const LogoButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;

    @media (min-width: 1024px) {
      display: none;
    }
`;

// Filter removed to keep logo white (assuming original is white/light)
// If the original is dark, we might need 'brightness(0) invert(1)'
const NavbarLogo = styled.img`
  height: 32px;
  object-fit: contain;
  filter: ${p => p.theme.isDark ? 'brightness(0) invert(1)' : 'brightness(0)'}; /* Adapts to theme */
`;

const MenuButton = styled.button`
  width: 40px;
  height: 40px;
  background: ${p => p.theme.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
  border: 1px solid ${p => p.theme.colors.border.main};
  cursor: pointer;
  border-radius: 10px;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  backdrop-filter: blur(8px);

  &:hover {
    background-color: ${p => p.theme.colors.background.selected};
  }

  /* Hamburger lines */
  span {
    display: block;
    width: 20px;
    height: 2px;
    background-color: ${p => p.theme.colors.text.primary};
    border-radius: 2px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Animation states for active menu */
  span:nth-child(1) {
    transform: ${p => p.$isOpen ? 'translateY(7px) rotate(45deg)' : 'none'};
  }
  span:nth-child(2) {
    opacity: ${p => p.$isOpen ? 0 : 1};
    transform: ${p => p.$isOpen ? 'translateX(-10px)' : 'none'};
  }
  span:nth-child(3) {
    transform: ${p => p.$isOpen ? 'translateY(-7px) rotate(-45deg)' : 'none'};
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;

const DesktopUserSection = styled.div`
  display: none;
  align-items: center;
  gap: 20px;

  @media (min-width: 1024px) {
    display: flex;
    margin-left: auto; /* Push to right */
    padding-right: 20px;
  }
`;

const DesktopUserButton = styled.button`
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: ${p => p.theme.colors.background.selected};
  }
`;

const GreetingText = styled.span`
  color: ${p => p.theme.colors.text.primary};
  font-size: 15px;
  font-weight: 600;
`;

const ProfileDropdown = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  right: 20px;
  background: ${p => p.theme.colors.background.paper};
  border: 1px solid ${p => p.theme.colors.border.main};
  border-radius: 12px;
  box-shadow: ${p => p.theme.shadows.lg};
  min-width: 200px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DropdownItem = styled.button`
  background: none;
  border: none;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.theme.colors.text.primary};
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${p => p.theme.colors.background.selected};
  }
`;

// --- COMPONENTE DE REACT ---

const Header = ({ showMenu, setShowMenu, onLogout }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isBusiness = location.pathname.includes('/business');
  const basePath = isBusiness ? '/business' : '/cross_border';

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <HeaderContainer>
      <HeaderContent>
        <LogoButton>
          <NavbarLogo
            src={navbarLogo}
            alt="NYBANQ Navbar Logo"
          />
        </LogoButton>

        <MenuButton
          onClick={() => setShowMenu(!showMenu)}
          aria-label="Open Menu"
          $isOpen={showMenu}
        >
          <span />
          <span />
          <span />
        </MenuButton>

        <DesktopUserSection ref={dropdownRef} style={{ position: 'relative' }}>
          <DesktopUserButton onClick={() => setShowProfileMenu(!showProfileMenu)}>
            <GreetingText>Hello, Apex Business</GreetingText>
            <ChevronDown size={16} color="currentColor" style={{ color: "inherit" }} />
          </DesktopUserButton>

          {showProfileMenu && (
            <ProfileDropdown>
              <DropdownItem onClick={() => { setShowProfileMenu(false); navigate(`${basePath}/settings`); }}>
                <User size={16} /> Profile
              </DropdownItem>
              <DropdownItem onClick={onLogout || (() => window.location.href = '/')}>
                <LogOut size={16} /> Log Out
              </DropdownItem>
            </ProfileDropdown>
          )}
        </DesktopUserSection>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;